import {
  boundingBoxToTuple,
  createElementFactory,
  getSliderStep,
  getValueAtPath,
  JsxgraphBaseWidget,
  type JsxgraphStatic,
  type JsxgraphThemeService,
  parseParamPath,
  parseExpressionThunk,
  ReactionEvaluator,
  WidgetEvent,
  type WidgetState,
} from '@examdojo/core/widget';
import type { CreatedElement, ElementContext } from './elements/element.model';
import { isParameterizedElement } from './elements/element.model';
import { ElementResolver } from './elements/element.resolver';
import { LabelCreator, PointCreator, LineCreator, PolygonCreator } from './elements/primitives';
import { DEFAULT_FEATURE_THEME, FeatureRegistry } from './elements/features';
import { FunctionRegistry } from './elements/functions/function.registry';
import { ValueContext } from './context/value-context';
import type { FunctionElementDef, GraphpadParams, GraphpadWidgetConfig } from './graphpad.model';
import { isFunctionElementDef } from './graphpad.model';

/**
 * GraphpadWidget is a declarative graph system that interprets config to create
 * functions, features, handles, derived elements, and relationships.
 *
 * Unlike per-function-type widgets, this single widget can render any combination
 * of supported elements based on the declarative config.
 */
export class GraphpadWidget extends JsxgraphBaseWidget<GraphpadWidgetConfig> {
  constructor(container: HTMLElement, jsxInstance: JsxgraphStatic, themeService: JsxgraphThemeService) {
    super(container, jsxInstance, themeService);
    this.id = `graphpad-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    // Initialize resolver and register all creators
    this.resolver = new ElementResolver();
    this.registerCreators();
  }

  readonly id: string;
  readonly type = 'graphpad';

  private config?: GraphpadWidgetConfig;
  private readonly createdElements: Map<string, CreatedElement> = new Map();
  private readonly resolver: ElementResolver;
  private readonly valueContext = new ValueContext();
  private reactionEvaluator?: ReactionEvaluator;

  protected getCurrentConfig(): GraphpadWidgetConfig | undefined {
    return this.config;
  }

  getState(): WidgetState {
    return {
      type: this.type,
      id: this.id,
      params: this.config?.params,
    };
  }

  resolveValue(path: string): number | string | undefined {
    if (this.valueContext.has(path)) {
      return this.valueContext.getValue(path);
    }
    if (this.valueContext.hasText(path)) {
      return this.valueContext.getTextValue(path);
    }
    return undefined;
  }

  init(config: GraphpadWidgetConfig): void {
    this.config = config;
    const params = config.params;
    const displayConfig = config.display;

    // Validate config - check for multiple handles on same function
    this.validateHandleConfig(params);

    // Get coordinate system config
    const coordinateSystem = displayConfig?.coordinateSystem;
    let boundingbox = boundingBoxToTuple(
      coordinateSystem?.boundingbox ?? { xStart: -10, xEnd: 10, yStart: -10, yEnd: 10 },
    );

    // Adjust bounding box for equal units when fixedAxis is set
    const fixedAxis = coordinateSystem?.fixedAxis;
    if (fixedAxis) {
      boundingbox = this.adjustBboxForFixedAxis(boundingbox, fixedAxis);
    }

    // Initialize board (background color auto-resolved from display.canvas.color by base class)
    this.initBoard({ boundingbox, axis: false });

    // Set up resize observer to maintain equal units when fixedAxis is configured
    if (fixedAxis) {
      this.setupFixedAxisObserver();
    }

    // Create coordinate system from display config
    this.createCoordinateSystem(coordinateSystem);

    // Create element factory for styled element creation
    const elementFactory = createElementFactory(this.board, this.container, this.themeService, {
      resolveValue: (path: string) => this.resolveValue(path),
      defaultPrecision: displayConfig?.math?.defaultPrecision,
    });

    // Create element context with factory and feature theme
    const context: ElementContext = {
      board: this.board,
      elements: this.createdElements,
      getParam: (path) => this.getParamValue(path, params),
      themeService: this.themeService,
      elementFactory,
      featureTheme: DEFAULT_FEATURE_THEME,
      container: this.container,
      onParamChange: (elementId, changedParams) => {
        this.emit(WidgetEvent.ParamChange, { elementId, params: changedParams });
        this.reactionEvaluator?.evaluate();
      },
      defaultPrecision: displayConfig?.math?.defaultPrecision,
      valueContext: this.valueContext,
    };

    // Resolve and create elements in dependency order
    const sortedIds = this.resolver.resolveDependencies(params);

    for (const elementId of sortedIds) {
      let def = params[elementId];
      const elementDisplay = displayConfig?.elements?.[elementId];

      // Resolve expression params to numbers for function elements
      if (isFunctionElementDef(def)) {
        def = this.resolveFunctionExpressions(def);
      }

      try {
        const created = this.resolver.createElement(elementId, def, elementDisplay, context);
        this.createdElements.set(elementId, created);
        this.valueContext.registerElement(elementId, created);

        // Register parameterized elements for UPDATE_PARAM support (enables animated parameter updates)
        // This includes functions, features (tangent, secant), and any element with getParams/setParams
        if (isParameterizedElement(created)) {
          this.registerParameterizedElement(elementId, created);
        }

        // Track JSXGraph objects in the base class objects map
        created.objects.forEach((obj, idx) => {
          this.objects.set(`${elementId}-${idx}`, obj);
        });
      } catch (error) {
        console.error(`Failed to create element '${elementId}':`, error);
      }
    }

    // Configure display steps from slider controls so expressions show clean values during animation
    this.configureDisplaySteps(config);

    // Initialize reaction evaluator after all elements are created
    const reactions = config.behavior?.reactions;
    if (reactions?.length) {
      this.reactionEvaluator = new ReactionEvaluator(
        reactions,
        (expr) => parseExpressionThunk(expr, this.valueContext),
        (action) => this.executeAction(action),
      );
      this.reactionEvaluator.evaluate();
    }

    // Initialize canvas selection controls (on-canvas radio buttons)
    this.initCanvasSelections(config.behavior?.controls);
  }

  override onParamAnimationStep(): void {
    this.reactionEvaluator?.evaluate();
  }

  getElement(elementId: string): CreatedElement | undefined {
    return this.createdElements.get(elementId);
  }

  override destroy(): void {
    this.reactionEvaluator = undefined;
    this.destroyCreatedElements(this.createdElements);
    super.destroy();
  }

  private registerCreators(): void {
    // Register function creators
    this.resolver.registerCreators(FunctionRegistry.getAll());

    // Register feature creators (includes derivative, intersection, roots, extrema, tangent, secant, handle)
    // Set handle drag callback to emit param changes before registering
    FeatureRegistry.setHandleDragCallback((elementId, params) => {
      this.emit(WidgetEvent.ParamChange, { elementId, params });
      this.reactionEvaluator?.evaluate();
    });
    this.resolver.registerCreators(FeatureRegistry.getAll());

    // Register primitive creators (labels, points, lines, polygons)
    this.resolver.registerCreators([new LabelCreator(), new PointCreator(), new LineCreator(), new PolygonCreator()]);
  }

  /**
   * Configure display snap steps on elements based on slider control configs.
   * This ensures expressions show clean step-aligned values during animation
   * while the curve still animates smoothly with full-precision values.
   */
  private configureDisplaySteps(config: GraphpadWidgetConfig): void {
    const controls = config.behavior?.controls;
    if (!controls) {
      return;
    }

    // Collect steps per element: { elementId: { paramName: step } }
    const stepsByElement = new Map<string, Record<string, number>>();

    for (const control of controls) {
      const sliders = control.type === 'slider' ? [control] : control.type === 'slider-group' ? control.sliders : [];

      for (const slider of sliders) {
        const parsed = parseParamPath(slider.param);
        if (!parsed.root) {
          continue;
        }

        let steps = stepsByElement.get(parsed.root);
        if (!steps) {
          steps = {};
          stepsByElement.set(parsed.root, steps);
        }
        steps[parsed.leaf] = getSliderStep(slider);
      }
    }

    for (const [elementId, steps] of stepsByElement) {
      this.createdElements.get(elementId)?.setDisplaySteps?.(steps);
    }
  }

  /**
   * Validate handle configuration at runtime.
   *
   * Note: When using GraphpadConfigBuilder, this constraint is enforced at compile-time.
   * This runtime check serves as a safety net for configs built manually or from JSON.
   *
   * Currently, only one handle per function is supported.
   */
  private validateHandleConfig(params: GraphpadParams): void {
    const handlesByFunction = new Map<string, string[]>();

    for (const [elementId, def] of Object.entries(params)) {
      if (def.type === 'handle') {
        const handleParams = def.params as { of: string; behavior: string } | undefined;
        const functionId = handleParams?.of;

        if (!functionId) {
          throw new Error(`Handle '${elementId}' is missing params.of`);
        }

        if (!handlesByFunction.has(functionId)) {
          handlesByFunction.set(functionId, []);
        }
        handlesByFunction.get(functionId)!.push(elementId);
      }
    }

    for (const [functionId, handleIds] of handlesByFunction) {
      if (handleIds.length > 1) {
        throw new Error(
          `Multiple handles on the same function are not supported. ` +
            `Function '${functionId}' has ${handleIds.length} handles: [${handleIds.join(', ')}]. ` +
            `Please use only one handle per function.`,
        );
      }
    }
  }

  /**
   * Resolve ValueExpression params in a function element definition to numbers.
   * String expressions are evaluated via ValueContext; numeric values pass through.
   */
  private resolveFunctionExpressions(def: FunctionElementDef): FunctionElementDef {
    if (!def.params) {
      return def;
    }
    const hasExpressions = Object.values(def.params).some((v) => typeof v === 'string');
    if (!hasExpressions) {
      return def;
    }

    const resolvedParams: Record<string, number> = {};
    for (const [key, value] of Object.entries(def.params)) {
      if (typeof value === 'string') {
        resolvedParams[key] = this.valueContext.createThunk(value)();
      } else {
        resolvedParams[key] = value;
      }
    }
    return { ...def, params: resolvedParams };
  }

  /**
   * Get a parameter value from a dot-notation path.
   * Supports paths like 'f.params.a' (element ID + property path)
   */
  private getParamValue(path: string, params: GraphpadParams): number {
    const value = getValueAtPath(params, path);
    return typeof value === 'number' ? value : 0;
  }
}
